import React from "react";
import "components/page/page.scss";
import { ButtonNew as Button } from "components/buttonNew";
import MultipleActionDropdown, {
  IMultipleActionDropdownProps,
} from "components/multipleActionDropdown/MultipleActionDropdown";
import SplitButton, { ISplitButtonProps } from "components/splitButton/SplitButton";
import { IconDefinition, IconName } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

export interface IPrimaryPageAction {
  action: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  content: JSX.Element | string;
}

export interface ISecondaryPageAction {
  action?: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  content: JSX.Element | string;
  type?: "default" | "primary" | "secondary" | "tertiary" | "quaternary" | "dark-gray" | "error" | "error-outline" | "text" | "link" | "link-color" | "close";
}

export interface IMultipleActionDropdownPageAction {
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  icon?: IconName | React.ReactNode;
  disabled?: boolean;
  dropdownProps: Omit<IMultipleActionDropdownProps, "children">;
}

export interface IActionDetails {
  icon: IconName;
  /**Add any optional icon props. Ex: spin or pulse for animations */
  iconProps?: Omit<FontAwesomeIconProps, "icon">;
  content: string;
  /**If this prop is passed, the action details text will be underlined. When hovered, a tooltip will be displayed with the content of this prop value*/
  toolTipContent?: string;
}

export interface ITextAction {
  icon?: IconDefinition;
  content: string;
  action: () => void;
  disabled?: boolean;
  hidden?: boolean;
}

interface IPageActionsProps {
  primaryAction?: IPrimaryPageAction;
  secondaryActions?: ISecondaryPageAction[];
  multipleActionDropdownAction?: IMultipleActionDropdownPageAction;
  splitButtonAction: ISplitButtonProps;
  actionDetails?: IActionDetails;
  textAction?: ITextAction;
}

function PageActions(props: IPageActionsProps) {
  const { primaryAction, secondaryActions, multipleActionDropdownAction, splitButtonAction, actionDetails, textAction } = props;

  return (
    <div className="ui-page-actions">
      {actionDetails &&
        //Show details with tool tip
        (actionDetails.toolTipContent ? (
          <OverlayTrigger
            placement="auto"
            overlay={<Tooltip id="leftMenuToolTip">{actionDetails?.toolTipContent}</Tooltip>}
          >
            <div className="ui-page-actions-details-container ui-page-actions-details-container-tooltip">
              <FontAwesomeIcon
                className={"ui-pages-action-details"}
                size="1x"
                icon={["far", actionDetails.icon]}
                {...actionDetails?.iconProps}
              />
              <p className="ui-pages-action-details">{actionDetails.content}</p>
            </div>
          </OverlayTrigger>
        ) : (
          //Display details without tool tip
          <div className="ui-page-actions-details-container">
            <FontAwesomeIcon
              className="ui-pages-action-details"
              size="1x"
              icon={["far", actionDetails.icon]}
              {...actionDetails?.iconProps}
            />
            <p className="ui-pages-action-details">{actionDetails.content}</p>
          </div>
        ))}
        {textAction && !textAction?.hidden ? (
          <Button size="medium" type="text" disabled={!!textAction?.disabled} onClick={textAction.action}>
            {textAction?.icon ? (
              <FontAwesomeIcon icon={textAction.icon} className="mr-2"/>
            ) : null}
          {textAction.content}
        </Button>
        ) : null}
      {secondaryActions?.map((item: ISecondaryPageAction, index: number) => (
        <Button key={index} type={item.type} onClick={item.action} disabled={!!item.disabled}>
          {item.content}
        </Button>
      ))}
      {multipleActionDropdownAction && (
        <MultipleActionDropdown
          {...multipleActionDropdownAction.dropdownProps}
          disabled={multipleActionDropdownAction.disabled}
        >
          <Button
            size="medium"
            type="secondary"
            disabled={!!multipleActionDropdownAction?.disabled}
            icon={
              typeof multipleActionDropdownAction.icon === "string" ? (
                <FontAwesomeIcon icon={["far", multipleActionDropdownAction.icon as IconName]} />
              ) : (
                multipleActionDropdownAction.icon
              )
            }
          >
            {multipleActionDropdownAction.label}
          </Button>
        </MultipleActionDropdown>
      )}
      {splitButtonAction && <SplitButton {...splitButtonAction}></SplitButton>}
      {primaryAction && (
        <Button size="medium" type="primary" disabled={!!primaryAction.disabled} onClick={primaryAction.action}>
          {primaryAction.content}
        </Button>
      )}
    </div>
  );
}

export default PageActions;
